// Parsing and formatting for a creator's monthly subscription price.
// The backend accepts a whole number of credits, at least 1.

export type PriceParseResult =
  | { ok: true; credits: number }
  | { ok: false; reason: string };

export const MIN_MONTHLY_PRICE_CREDITS = 1;

/**
 * Turns what the creator typed into a whole number of credits.
 * Rejects blanks, decimals, signs, exponents and anything non-numeric so the
 * backend never sees a value it would answer with a 400.
 */
export function parseMonthlyPrice(raw: string): PriceParseResult {
  const trimmed = raw.trim();

  if (!trimmed) {
    return { ok: false, reason: 'Enter a monthly price.' };
  }

  if (!/^\d+$/.test(trimmed)) {
    return { ok: false, reason: 'Use a whole number of credits.' };
  }

  const credits = Number(trimmed);

  if (credits < MIN_MONTHLY_PRICE_CREDITS) {
    return { ok: false, reason: 'Price must be at least 1 credit.' };
  }

  if (!Number.isSafeInteger(credits)) {
    return { ok: false, reason: 'That price is too high.' };
  }

  return { ok: true, credits };
}

/** Human-readable price for the current plan, or a hint when none is set. */
export function formatMonthlyPrice(credits: number | null): string {
  if (credits === null) {
    return 'Not set yet';
  }
  return `${credits} ${credits === 1 ? 'credit' : 'credits'} / month`;
}

/** Label for the fan-facing subscribe button, e.g. "Subscribe for 10 credits/month". */
export function formatSubscribeCta(credits: number): string {
  return `Subscribe for ${credits} ${credits === 1 ? 'credit' : 'credits'}/month`;
}
